/**
 * @fileoverview Google Ad component for displaying ads using Google Publisher Tag (GPT).
 * @see https://developers.google.com/publisher-tag
 * @see https://docs.google.com/spreadsheets/d/1OXX1gA_vR67ZQFRymdE5eWmyFWd21cXKIpkqvQShanM/edit?gid=1306078004#gid=1306078004
 */

import clsx from "clsx";
import { useEffect, useState } from "react";

import {
  adSizeMappings,
  adSlotSizes,
  adTargetings,
  GoogleAdTypeEnum,
  mapAdSlotId,
  useComposedAdUnitPath,
} from "./config";

// export type with component
export { GoogleAdTypeEnum } from "./config";

export type GoogleAdProps = {
  needRefresh?: boolean;
  className?: string;
  type: GoogleAdTypeEnum;
  onSlotRenderEnded?: (event: googletag.events.SlotRenderEndedEvent) => void;
};

/**
 * @tutorial https://developers.google.com/publisher-tag/guides/get-started // google publisher tag
 */
export default function GoogleAd({
  needRefresh = false,
  className = "",
  type,
  onSlotRenderEnded,
}: GoogleAdProps) {
  const adSlotId = mapAdSlotId(type);
  const adUnitPath = useComposedAdUnitPath(type);

  const adSlotSize = adSlotSizes[type];
  const adSizeMapping = adSizeMappings[type];
  const adTarget = adTargetings[type];

  const [isAdEmpty, setIsAdEmpty] = useState(true);

  useEffect(() => {
    let slot: googletag.Slot | null;

    try {
      // check if slot DOM is ready
      if (!document.getElementById(adSlotId)) {
        console.error(`Ad slot ${adSlotId} not found`);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      window.googletag = window.googletag || { cmd: [] };
      googletag.cmd.push(() => {
        // check if slot already exists, if so, destroy it

        // TODO comment first here, will overrider dfp_preview ads
        const slots = googletag.pubads().getSlots();
        const exactSlot = slots.find((i) => i.getSlotElementId() == adSlotId);
        if (exactSlot) {
          googletag.destroySlots([exactSlot]);
        }

        // Define an ad slot
        slot = googletag.defineSlot(adUnitPath, adSlotSize, adSlotId);
        if (slot) {
          slot.addService(googletag.pubads());

          // Define size mapping for the ad slot
          if (adSizeMapping) slot.defineSizeMapping(adSizeMapping);
          // Set targeting for the ad slot
          adTarget?.forEach((i) =>
            slot?.setConfig({ targeting: { [i.key]: i.value } }),
          );

          // set up event listener for slot render ended
          googletag.pubads().addEventListener("slotRenderEnded", (event) => {
            if (event.slot.getSlotElementId() === adSlotId) {
              console.log(
                "Ad slotRenderEnded:",
                event.slot.getSlotElementId(),
                event.slot.getAdUnitPath(),
              );
              onSlotRenderEnded?.(event);

              setIsAdEmpty(event.isEmpty);
              if (event.isEmpty) {
                console.log("Ad slot is empty:", event.slot.getSlotElementId());
              }
            }
          });

          // Display the ad slot
          googletag.display(adSlotId);
          if (type === GoogleAdTypeEnum.LB1) {
            const googletagWithSlots = window.googletag as typeof googletag & {
              slots?: Record<string, googletag.Slot>;
            };
            googletagWithSlots.slots = googletagWithSlots.slots || {};
            googletagWithSlots.slots[type] = slot;
            window.load_ad_tag = 1;
          }

          if (needRefresh) {
            googletag.pubads().refresh([slot], { changeCorrelator: false });
          }
        }
      });
    } catch (error) {
      console.error("Google Ad Error", error);
    }

    return () => {
      // Cleanup the ad slot
      if (slot) {
        googletag.destroySlots([slot]);
      }
    };
  }, [
    adUnitPath,
    adSlotSize,
    adSlotId,
    adSizeMapping,
    adTarget,
    onSlotRenderEnded,
    setIsAdEmpty,
    needRefresh,
    type,
  ]);

  return (
    <div
      id={`${adSlotId}-wrapper`}
      className={clsx("w-full justify-center", className)}
      style={{ display: isAdEmpty ? "none" : "flex" }}
    >
      <div id={adSlotId} className="flex h-max" />
    </div>
  );
}
